import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { type PaymentBreakdown } from '../../../../types/PaymentBreakdown';
import { type WidgetProps } from '../../../../types/WidgetProps';
import { type GooglePayButtonConfig } from '../types';
import { t } from '../../../../utils/i18n';
import { type BiLogger, NA } from '../../../Provider/BiLogger/BiLogger';
import { amountToLong } from '../../../../utils/amountToLong';
import { ecwGettingPaymentDataFromVertical } from '../../../../utils/bi/events/biEvents';
import { getOrderTypeByItems } from '../../../../utils/bi/getOrderTypeByItems';
import { getTransactionInfo } from './getTransactionInfo';

export type GetInitialPaymentDataReturn = ReturnType<GooglePayButtonConfig['onPaymentDataChanged']>;

interface GetInitialTransactionInfo {
  paymentAmount: string;
  paymentBreakdown: PaymentBreakdown;
  currency: string;
  countryCode: string;
  sessionId: string;
  getInitialPaymentData: WidgetProps['getInitialPaymentData'];
  biLogger: BiLogger;
  requestShipping: boolean;
}

/**
 * @attention - this fn should be run `always with await` because `topProps.getInitialPaymentData` can do some side effects
 * like 'Cart.create()'. After that we can call another methods like `onShippingContactSelected` | `onPaymentAuthorized`
 */
export const getInitialTransactionInfo = async (
  params: GetInitialTransactionInfo,
): Promise<GetInitialPaymentDataReturn> => {
  if (!params.getInitialPaymentData) {
    return {};
  }

  const biLoggerCommonParams = {
    isPartner: undefined as any, // TODO[bi]
    cartId: undefined as any, // TODO[bi]
    productid: undefined as any, // TODO[bi]
    orderSnapshotId: undefined as any, // TODO[bi]
    transactionId: undefined as any, // TODO[bi]
  } as const;

  try {
    params.biLogger.report(ecwGettingPaymentDataFromVertical, {
      amount: amountToLong(params.paymentAmount),
      currency: params.currency,
      paymentProvider: PaymentProvider.WixPayUS,
      paymentmethod: PaymentMethod.GooglePay,
      stepName: 'initialization',
      orderType: undefined as any, // TODO[bi]
      errorReason: undefined as any, // TODO[bi]
      result: undefined as any, // TODO[bi]
      shippingAmount: undefined as any, // TODO[bi]
      taxAmount: undefined as any, // TODO[bi]
      discountAmount: undefined as any, // TODO[bi]
      ...biLoggerCommonParams,
    });

    const { paymentAmount, paymentBreakdown, orderItems } = await params.getInitialPaymentData();

    if (!paymentAmount && !paymentBreakdown) {
      void params.biLogger.report(ecwGettingPaymentDataFromVertical, {
        ...biLoggerCommonParams,
        paymentProvider: PaymentProvider.WixPayUS,
        paymentmethod: PaymentMethod.GooglePay,
        stepName: 'result',
        result: 'failure',
        errorReason: `Vertical did't return paymentAmount && paymentBreakdown from getInitialPaymentData`,
        currency: params.currency,
        orderType: getOrderTypeByItems(orderItems),
        amount: NA,
        shippingAmount: NA,
        taxAmount: NA,
        discountAmount: NA,
      });

      return {
        error: {
          intent: 'PAYMENT_METHOD',
          reason: 'OTHER_ERROR',
          message: t('pm.onPaymentInitialization.unknownError'),
        },
      };
    }

    void params.biLogger.report(ecwGettingPaymentDataFromVertical, {
      amount: amountToLong(paymentAmount),
      paymentProvider: PaymentProvider.WixPayUS,
      currency: params.currency,
      paymentmethod: PaymentMethod.GooglePay,
      stepName: 'result',
      result: 'success',
      shippingAmount: amountToLong(paymentBreakdown.shipping),
      taxAmount: amountToLong(paymentBreakdown.tax),
      discountAmount: amountToLong(paymentBreakdown.discount),
      orderType: getOrderTypeByItems(orderItems),
      errorReason: NA,
      ...biLoggerCommonParams,
    });

    const transactionInfo = getTransactionInfo({
      paymentAmount: paymentAmount || params.paymentAmount,
      paymentBreakdown: paymentBreakdown || params.paymentBreakdown,
      currency: params.currency,
      countryCode: params.countryCode,
      sessionId: params.sessionId,
      requestShipping: params.requestShipping,
      orderItems,
    });

    return {
      newTransactionInfo: transactionInfo,
    };
  } catch (error) {
    params.biLogger.report(ecwGettingPaymentDataFromVertical, {
      paymentProvider: PaymentProvider.WixPayUS,
      paymentmethod: PaymentMethod.GooglePay,
      stepName: 'result',
      result: 'failure',
      errorReason: error.message || JSON.stringify(error),
      amount: amountToLong(params.paymentAmount),
      currency: params.currency,
      orderType: undefined as any, // TODO[bi]
      shippingAmount: undefined as any, // TODO[bi]
      taxAmount: undefined as any, // TODO[bi]
      discountAmount: undefined as any, // TODO[bi]
      ...biLoggerCommonParams,
    });
    return {
      error: {
        intent: 'PAYMENT_METHOD',
        reason: 'OTHER_ERROR',
        message: t('pm.onPaymentInitialization.unknownError'),
      },
    };
  }
};
